import React from "react";
import { LEAGUE_CARD_TEAMS, VIP_LEAGUE_KEYS } from "../../Common/constants";
import {
  getTranslations,
  getWebPurifyStatus,
  isMobile,
} from "../../Common/utils";
import { getTeamDisplayRank } from "../../Common/utils/league";
import LeagueLogo from "../Cloudinary/LeagueLogo";
import LeagueTypeIcon from "../Cloudinary/LeagueTypeIcon";
import LeagueTeamScore from "../LeagueTeamScore";
import BackSection from "../BackSection";
import { CtaButton } from "../Buttons";
import MiniLeagueTabs from "../MiniLeagueTabs";
import HeadToHeadTeamScore from "../HeadToHeadTeamScore";
import { ProvisionalPointsBadge } from "../ProvisionalPointsBadge";
import { ProvisionalPtsTooltip } from "../ProvisionalPtsTooltip";

const NewLeagueHeader = ({
  league = null,
  isHeadToHead = false,
  backButtonClick = () => {},
  onShareLeague = () => {},
  onLeagueSettings = () => {},
  onAddTeam = () => {},
  showShareButton = true,
  showSettingsButton = true,
  isUserLoggedIn = false,
  hasUserJoined = false,
  onJoinLeague = () => {},
  activeTab = "Results",
  onTabChange = () => {},
  leagueId = "",
  isMiniLeague = false,
  isSponsoredLeague = false,
  showPrizesTab = true,
  isSponsorAndPrivate = false,
  isProvisional = false,
}) => {
  const currentLeague = league;
  const allUserTeamsJoined =
    Number(currentLeague?.teams?.length) === Number(currentLeague?.no_of_teams);

  function isStarterLeague(leagueType) {
    switch (leagueType) {
      case "Global":
      case "Driver":
      case "Team":
      case "Country":
        return true;
      default:
        return false;
    }
  }
  const isReportFlag = currentLeague?.is_report_flag === 1;
  const isAdmin = currentLeague?.is_admin === 1;
  const leagueType = currentLeague?.leagueType || currentLeague?.league_type;
  const leagueName = currentLeague?.league_name || currentLeague?.league_name;
  const memberCount =
    currentLeague?.formatted_member_count ||
    currentLeague?.member_count ||
    "00k";
  const isMiniLeagueValue =
    isMiniLeague || leagueType === "Mini" || leagueType === "mini";
  const isVipLeague = !!league?.is_vip;
  const isSponsoredLeagueValue =
    isSponsoredLeague || !!league?.is_sponcer || !!league?.isSponsor;

  const lockDateTime = currentLeague?.lock_datetime_utc;
  const isLockPassed = lockDateTime
    ? new Date(lockDateTime).getTime() <= Date.now()
    : false;
  const isMiniNotJoinable =
    isMiniLeagueValue && (!isUserLoggedIn || !hasUserJoined);

  const webPurifyStatus = getWebPurifyStatus(
    league?.is_profane_name_flag,
    league?.is_system_name_upd
  );

  return (
    <div className="si-newLeagueHeader">
      <div className="si-main__container">
        <BackSection
          backButtonClick={backButtonClick}
          hasRhsSection={showShareButton || showSettingsButton}
          rhsSection={
            <>
              {showShareButton && (
                <CtaButton
                  onlyIcon={true}
                  variant="ghost"
                  icon={"f1i-share"}
                  onClick={onShareLeague}
                >
                  {getTranslations("cta_share_league", "Share League")}
                </CtaButton>
              )}
              {showSettingsButton && (
                <CtaButton
                  onlyIcon={true}
                  variant="ghost"
                  icon={"f1i-customise"}
                  onClick={onLeagueSettings}
                >
                  {getTranslations("cta_league_settings", "League Settings")}
                </CtaButton>
              )}
            </>
          }
        />

        <div className="si-newLeagueHeader__content">
          <div className="si-newLeagueHeader__lhs">
            <div className="si-newLeagueHeader__logo-wrapper">
              {isStarterLeague(leagueType) ? (
                <LeagueLogo
                  leagueType={leagueType}
                  leagueId={currentLeague?.league_code}
                  imageName={currentLeague?.league_code}
                  isGlobal={true}
                />
              ) : isMiniLeagueValue || isSponsoredLeagueValue ? (
                <LeagueLogo
                  leagueType={
                    isMiniLeagueValue
                      ? "mini"
                      : isSponsorAndPrivate
                        ? "private"
                        : "public"
                  }
                  leagueId={leagueId}
                />
              ) : isVipLeague ? (
                <LeagueLogo
                  leagueType={VIP_LEAGUE_KEYS.cloudinaryLeagueType}
                  leagueId={leagueId}
                />
              ) : (
                <LeagueTypeIcon
                  type={leagueType}
                  leagueId={leagueId}
                  webPurifyStatus={webPurifyStatus}
                  showProfanityIcon={isAdmin && !isReportFlag}
                  isReportFlag={isAdmin && isReportFlag}
                />
              )}
            </div>
            <div className="si-newLeagueHeader__info">
              <h1 className="si-newLeagueHeader__title">
                {decodeURIComponent(leagueName)}
              </h1>
              <div className="si-newLeagueHeader__meta">
                <span className="si-newLeagueHeader__type">
                  {isVipLeague
                    ? VIP_LEAGUE_KEYS.leagueType
                    : leagueType?.toUpperCase()}
                </span>
                <span className="si-newLeagueHeader__separator"></span>
                <span className="si-newLeagueHeader__teams-count">
                  {getTranslations("league_card_total_team", "Total Teams:")}{" "}
                  {memberCount}
                </span>
                {isProvisional && (
                  <span>
                    {!isMobile() ? (
                      <ProvisionalPointsBadge
                        variant="chip-slim"
                        isInverted={true}
                      />
                    ) : (
                      <ProvisionalPtsTooltip isTooltipTrue={true} />
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="si-newLeagueHeader__rhs">
            {isMiniNotJoinable && !isLockPassed ? (
              <CtaButton variant="accent" onClick={onJoinLeague}>
                {getTranslations("mini_landing_join_cta", "Join")}
              </CtaButton>
            ) : isMiniNotJoinable && isLockPassed ? null : isHeadToHead ? (
              <div className="si-newLeagueHeader__headToHead">
                <HeadToHeadTeamScore
                  type="W"
                  score={currentLeague?.battle_result?.[0]?.W || 0}
                />
                <HeadToHeadTeamScore
                  type="L"
                  score={currentLeague?.battle_result?.[0]?.L || 0}
                />
                <HeadToHeadTeamScore
                  type="D"
                  score={currentLeague?.battle_result?.[0]?.D || 0}
                />
              </div>
            ) : (
              <>
                {!isSponsorAndPrivate && (
                  <div className="si-newLeagueHeader__teams">
                    {LEAGUE_CARD_TEAMS.map((team) => {
                      const currentTeam = currentLeague?.teams?.find(
                        (t) => t?.team_no === team
                      );

                      if (!currentTeam && allUserTeamsJoined) {
                        return null;
                      }

                      if (isStarterLeague(leagueType) && !currentTeam) {
                        return null;
                      }

                      return (
                        <LeagueTeamScore
                          key={team}
                          teamNumber={team}
                          score={getTeamDisplayRank(
                            currentTeam?.trend,
                            currentTeam?.formatted_cur_rank
                          )}
                          trend={currentTeam?.trend}
                          isJoined={!!currentTeam}
                          onAddTeam={() => onAddTeam(currentLeague)}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {(isMiniLeagueValue || isSponsoredLeagueValue) && (
          <MiniLeagueTabs
            activeTab={activeTab}
            onTabChange={onTabChange}
            showPrizesTab={showPrizesTab}
          />
        )}
      </div>
    </div>
  );
};

export default NewLeagueHeader;
